import React from 'react';
import { formatDateTimeWithSeconds } from '@redbax/shared';
import { cn } from '../lib/utils';

/**
 * A timestamp that rolls from the old value to the new one when it changes.
 *
 * The change is expected to arrive from a live push (device heartbeats over the
 * `/bff/hubs/devices` hub) — this component never runs a clock of its own, it only
 * reacts to `value` changing. Characters that stay the same keep their DOM node, so
 * only the digits that actually moved animate.
 *
 * Motion collapses to a plain swap under `prefers-reduced-motion: reduce`.
 */

const ROLL_MS = 180;
const STAGGER_MS = 20;
const FADE_MS = 200;

type TimestampInput = string | number | Date | null | undefined;

interface AnimatedTimestampProps {
  value: TimestampInput;
  /** Defaults to the platform `dd-MMM-yyyy HH:mm:ss` style. */
  format?: (value: TimestampInput) => string;
  /** Shown when there is no value at all (e.g. a device that has never checked in). */
  fallback?: string;
  className?: string;
}

interface RollState {
  text: string;
  prev: string | null;
  gen: number;
}

function toIso(value: TimestampInput): string | undefined {
  if (value === null || value === undefined || value === '') return undefined;
  if (typeof value === 'string') return value;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

export function AnimatedTimestamp({
  value,
  format = formatDateTimeWithSeconds,
  fallback = '—',
  className,
}: AnimatedTimestampProps) {
  const hasValue = value !== null && value !== undefined && value !== '';
  const text = hasValue ? format(value) : fallback;

  const [state, setState] = React.useState<RollState>({ text, prev: null, gen: 0 });

  // Derive the transition from the incoming prop — the render React discards here is
  // replaced immediately, before anything reaches the DOM.
  if (state.text !== text) {
    setState((s) => ({ text, prev: s.text, gen: s.gen + 1 }));
  }

  const { text: current, prev, gen } = state;
  const rolling = prev !== null && prev.length === current.length;
  const changedCount = rolling
    ? current.split('').filter((ch, i) => prev![i] !== ch).length
    : 0;
  const duration = rolling ? ROLL_MS + changedCount * STAGGER_MS : FADE_MS;

  React.useEffect(() => {
    if (prev === null) return;
    const timer = setTimeout(
      () => setState((s) => (s.gen === gen ? { ...s, prev: null } : s)),
      duration,
    );
    return () => clearTimeout(timer);
  }, [gen, prev, duration]);

  let order = 0;

  return (
    <time
      data-testid="animated-timestamp"
      data-transition={prev === null ? undefined : rolling ? 'roll' : 'fade'}
      dateTime={hasValue ? toIso(value) : undefined}
      aria-label={current}
      aria-live="off"
      className={cn(
        'inline-flex items-baseline whitespace-pre tabular-nums',
        prev !== null && !rolling && 'animate-fade-in',
        className,
      )}
    >
      {current.split('').map((ch, i) => {
        const outgoing = rolling && prev![i] !== ch ? prev![i] : null;

        if (outgoing === null) {
          return (
            <span key={`${i}:${ch}`} data-char-index={i} className="inline-block">
              {ch}
            </span>
          );
        }

        const delay = `${order++ * STAGGER_MS}ms`;
        return (
          <span key={`${i}:${gen}:${ch}`} data-char-index={i} className="relative inline-block">
            <span
              aria-hidden="true"
              data-role="out"
              className="absolute inset-0 animate-roll-out"
              style={{ animationDelay: delay }}
            >
              {outgoing}
            </span>
            <span data-role="in" className="inline-block animate-roll-in" style={{ animationDelay: delay }}>
              {ch}
            </span>
          </span>
        );
      })}
    </time>
  );
}
